import { useStrings } from "@1js/localization-components";
import {
  Skeleton,
  SkeletonItem,
  ToggleButton,
} from "@fluentui/react-components";
import type { FluentIconsProps } from "@fluentui/react-icons";
import {
  Bookmark20Filled,
  Bookmark20Regular,
  bundleIcon,
} from "@fluentui/react-icons";
import * as React from "react";
import type { Appearance } from "../data";
import { YourPromptsLabel } from "./BookmarkToggleButton.strings";
import { useStyles } from "./BookmarkToggleButton.styles";

type BookmarkToggleButtonProps = {
  /**
   * Optional appearance to be applied to control icon style
   */
  appearance?: Appearance;
  /**
   * Optional class name to be applied to the root element.
   */
  className?: string;

  /**
   * Optional loading state to be applied to the root element. If true, a skeleton view will be rendered.
   */
  isLoading?: boolean;

  /**
   * Optional loading state to be applied to the root element. If true, button will be rendered as toggled.
   */
  showBookmarkOnly?: boolean;

  /**
   * Callback function that is invoked when the button is clicked.
   */
  onClicked: () => void;
};

const BookmarkIcon = bundleIcon(Bookmark20Filled, Bookmark20Regular);

export const BookmarkToggleButton: React.FC<BookmarkToggleButtonProps> = ({
  appearance,
  className,
  isLoading,
  showBookmarkOnly,
  onClicked,
}) => {
  const localizations = useStrings({
    YourPromptsLabel,
  });

  const styles = useStyles(appearance)();
  const favoriteIconStyleProps: FluentIconsProps = {
    filled: showBookmarkOnly,
    className: showBookmarkOnly ? styles.iconFilled : styles.icon,
  };

  return (
    <>
      {isLoading ? (
        <Skeleton aria-label="Loading Content">
          <SkeletonItem size={32} className={styles.skeleton} />
        </Skeleton>
      ) : (
        <ToggleButton
          size="medium"
          className={className}
          aria-pressed={showBookmarkOnly}
          icon={<BookmarkIcon {...favoriteIconStyleProps} />}
          iconPosition="before"
          data-testid="bookmark-filter-toggle-button"
          onClick={onClicked}
        >
          {localizations.YourPromptsLabel()}
        </ToggleButton>
      )}
    </>
  );
};
